import React from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useTheme } from '../../../context/ThemeContext';
import AdminJurisdictionsTab from '../components/AdminJurisdictionsTab';
import styles from './AdminDashboardPage.module.css';
import AdminErrorBanner from '../components/AdminErrorBanner';
import {
  UsersSection,
  DocumentsSection,
  TranscriptsSection,
  AdminsSection,
  ProfileSection,
  AccountSection,
  MessagesSection,
} from '../components/dashboard';
import { StatisticsPage } from '../components/statistics';
import { DashboardLayout, adminNavItems, adminPageItems, type SectionKey } from '../components/layout';
import { useAdminData } from '../hooks/useAdminData';
import { useAdminForms } from '../hooks/useAdminForms';
import { useAdminUtils } from '../hooks/useAdminUtils';
import { useAdminNavigation } from '../hooks/useAdminNavigation';
import EnterpriseSearchPage from '../../enterprise/pages/EnterpriseSearchPage';
import EnterpriseChatPage from '../../enterprise/pages/EnterpriseChatPage';
import EnterpriseChatHistoryPage from '../../enterprise/pages/EnterpriseChatHistoryPage';
import EnterpriseSearchHistoryPage from '../../enterprise/pages/EnterpriseSearchHistoryPage';
import EnterpriseRegulationsPage from '../../enterprise/pages/EnterpriseRegulationsPage';
import EnterpriseNewslettersPage from '../../enterprise/pages/EnterpriseNewslettersPage';
import EnterpriseChartsPage from '../../enterprise/pages/EnterpriseChartsPage';
import RegulationsBreakdownPage from '../../dashboard/RegulationsBreakdownPage';
import TrendingTopicsPanel from '../../dashboard/components/TrendingTopicsPanel';
import { useDashboardData, type TimeRangeType } from '../../dashboard/hooks/useDashboardData';

const ALL_ADMIN_SECTIONS = Array.from(new Set<SectionKey>([
  ...adminNavItems.map((item) => item.key),
  ...adminPageItems.map((item) => item.key),
  'profile',
  'account',
]));

const AdminTrendingWorkspace: React.FC = () => {
  const { token } = useAuth();
  const { theme } = useTheme();
  const selectedTimeRange: TimeRangeType = '30D';

  const getDateRangeForAPI = React.useCallback(() => ({ days: 30 }), []);

  const { chatTrendingTopics, isLoadingChatTopics } = useDashboardData(token, {
    selectedJurisdiction: 'All',
    selectedTimeRange,
    dateFilterType: 'last_action_date',
    customStartDate: '',
    customEndDate: '',
    selectedCountry: '',
    selectedContinent: 'All',
    activeFeedTab: 'trending',
    getDateRangeForAPI,
  });

  return (
    <section className={styles.section}>
      <TrendingTopicsPanel
        isLoading={isLoadingChatTopics}
        topics={chatTrendingTopics}
        theme={theme}
      />
    </section>
  );
};

export default function AdminDashboardPage() {
  // Stamp logout_at when the admin closes/hides the tab so session duration
  // metrics are accurate without requiring an explicit logout click.
  React.useEffect(() => {
    const sendSessionEnd = () => {
      const token = window.localStorage.getItem('policyora_token');
      if (!token) return;
      const baseUrl = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000';
      const fd = new FormData();
      fd.append('access_token', token);
      navigator.sendBeacon(`${baseUrl}/api/v1/auth/session-end`, fd);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') sendSessionEnd();
    };

    window.addEventListener('pagehide', sendSessionEnd);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('pagehide', sendSessionEnd);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);
  // Custom hooks for separation of concerns
  const {
    stats,
    documents,
    transcripts,
    users,
    admins,
    dashboardData,
    dashboardLoading,
    loading,
    secondaryLoading,
    error,
    adminProfile,
    requireToken,
    refreshAdminData,
    addDocument,
    addTranscript,
    updateProfile,
  } = useAdminData();

  const forms = useAdminForms(requireToken);
  
  const {
    formatAdminText,
    formatAdminDate,
    profileMeta,
    userCategoryData,
  } = useAdminUtils(adminProfile, stats, documents, transcripts, users, admins);

  const {
    sidebarCollapsed,
    setSidebarCollapsed,
    activeSection,
    selectedRegulation,
    setSelectedRegulation,
    userPreviewActive,
    handleNavClick,
    handleLogout,
    switchToAdminView,
    switchToUserView,
  } = useAdminNavigation();

  const location = useLocation();
  const isRegulationsBreakdown = location.pathname.includes('/regulations-breakdown');

  const currentAdminSummary = React.useMemo(
    () => admins.find((admin) => admin.email?.toLowerCase() === (adminProfile?.email || '').toLowerCase()) ?? null,
    [admins, adminProfile]
  );

  const allowedSections = React.useMemo(() => {
    // While admins list is still loading, return empty so sidebar shows nothing
    // until we know exactly which sections this admin is allowed to see.
    if (secondaryLoading) return [] as SectionKey[];

    if (!currentAdminSummary?.custom_role_id || !currentAdminSummary.allowed_pages?.length) {
      return ALL_ADMIN_SECTIONS;
    }

    const allowed = new Set<SectionKey>(['profile', 'account']);
    currentAdminSummary.allowed_pages.forEach((page) => {
      if (ALL_ADMIN_SECTIONS.includes(page as SectionKey)) {
        allowed.add(page as SectionKey);
      }
    });
    return Array.from(allowed);
  }, [secondaryLoading, currentAdminSummary]);

  React.useEffect(() => {
    if (!loading && allowedSections.length > 0 && !allowedSections.includes(activeSection)) {
      handleNavClick(allowedSections[0]);
    }
  }, [loading, allowedSections, activeSection, handleNavClick]);

  // Loading state
  if (loading) {
    return (
      <div className={styles.loadingShell}>
        <div className={styles.spinner}></div>
        <p>Loading admin workspace…</p>
      </div>
    );
  }

  // NOTE: the chat section renders INSIDE the standard admin DashboardLayout
  // (see `case 'chat'` below), exactly like Search / Chat History / Search
  // History. It previously short-circuited to a separate full ChatShell, which
  // jarringly swapped the entire admin sidebar/shell when navigating to Chat;
  // keeping it in DashboardLayout makes navigation consistent across sections.

  // Error state
  if (error || !stats) {
    return (
      <div className={styles.loadingShell}>
        <AdminErrorBanner
          message={error || 'We could not load the admin dashboard.'}
          details="Please refresh or try again in a few seconds."
          onRetry={refreshAdminData}
          align="center"
        />
      </div>
    );
  }

  // Section content renderer
  const getSectionContent = (): React.ReactNode => {
    // Admins list hasn't loaded yet — don't render anything until we know the role
    if (secondaryLoading) return null;

    const token = window.localStorage.getItem('policyora_token') || undefined;

    const currentSection: SectionKey = isRegulationsBreakdown ? 'charts' : activeSection;

    if (!allowedSections.includes(currentSection)) {
      return (
        <section className={styles.section}>
          <AdminErrorBanner
            message="Access restricted for this role."
            details="This admin account does not have permission to open the requested section."
            align="start"
          />
        </section>
      );
    }

    switch (currentSection) {
      case 'overview':
        return (
          <StatisticsPage
            token={token}
            preloadedStats={stats ?? undefined}
            preloadedTrends={dashboardLoading ? undefined : dashboardData.trends}
            preloadedJurisdictionStats={dashboardLoading ? undefined : dashboardData.jurisdictions}
          />
        );
      
      case 'jurisdictions':
        return (
          <section className={styles.section}>
            <AdminJurisdictionsTab
              totalJurisdictions={stats?.jurisdictions?.total ?? 0}
              preloadedJurisdictionStats={dashboardLoading ? undefined : dashboardData.jurisdictions}
            />
          </section>
        );
      
      case 'users':
        return (
          <UsersSection
            userCategoryData={userCategoryData}
            totalUsers={stats.users.users}
            totalAdmins={stats.users.admins}
            currentAdmin={currentAdminSummary}
          />
        );
      
      case 'documents':
        return (
          <DocumentsSection
            error={forms.documentError}
            loading={forms.documentUploadLoading}
            onSubmit={(e) => forms.handleDocumentUpload(e, addDocument)}
          />
        );
      
      case 'transcripts':
        return (
          <TranscriptsSection
            error={forms.transcriptError}
            successMessage={forms.transcriptSuccessMessage}
            newTranscriptUrl={forms.newTranscriptUrl}
            newTranscriptLanguage={forms.newTranscriptLanguage}
            transcriptPreview={forms.transcriptPreview}
            previewLoading={forms.transcriptPreviewLoading}
            uploadLoading={forms.transcriptUploadLoading}
            onUrlChange={forms.setNewTranscriptUrl}
            onLanguageChange={forms.setNewTranscriptLanguage}
            onGenerateTranscript={forms.handleTranscriptCreate}
            onUploadTranscript={() => forms.handleTranscriptUpload(addTranscript)}
            onClearPreview={() => {
              forms.setTranscriptPreview(null);
              forms.setTranscriptSuccessMessage(null);
            }}
          />
        );
      
      case 'admins':
        return (
          <AdminsSection
            admins={admins}
            token={requireToken()}
            currentAdmin={currentAdminSummary}
            newAdmin={forms.newAdmin}
            adminCreationStatus={forms.adminCreationStatus}
            adminCreationError={forms.adminCreationError}
            credentialsStatus={forms.credentialsStatus}
            credentialsError={forms.credentialsError}
            onInputChange={forms.handleAdminInputChange}
            onAdminCreate={(e) => forms.handleAdminCreate(e, refreshAdminData)}
            onSendCredentials={forms.handleSendAdminCredentials}
            formatAdminText={formatAdminText}
            formatAdminDate={formatAdminDate}
            onAdminRefresh={refreshAdminData}
            onBack={() => handleNavClick('users')}
          />
        );

      case 'profile':
        return (
          <ProfileSection
            profileMeta={profileMeta}
            editingProfileName={forms.editingProfileName}
            profileNameInput={forms.profileNameInput}
            profileUpdateError={forms.profileUpdateError}
            profileUpdateLoading={forms.profileUpdateLoading}
            userPreviewActive={userPreviewActive}
            currentAdmin={currentAdminSummary}
            onNameInputChange={forms.setProfileNameInput}
            onProfileEditStart={() => forms.handleProfileEditStart(adminProfile?.full_name ?? '')}
            onProfileEditCancel={forms.handleProfileEditCancel}
            onProfileSave={(e) => forms.handleProfileSave(e, updateProfile)}
            onSwitchAdminView={switchToAdminView}
            onSwitchUserView={switchToUserView}
          />
        );
      
      case 'account':
        return (
          <AccountSection
            passwordForm={forms.passwordForm}
            passwordStatus={forms.passwordStatus}
            passwordMessage={forms.passwordMessage}
            deleteAccountError={forms.deleteAccountError}
            deleteAccountConfirm={forms.deleteAccountConfirm}
            deleteAccountLoading={forms.deleteAccountLoading}
            onPasswordInputChange={forms.handlePasswordInputChange}
            onPasswordSubmit={forms.handlePasswordSubmit}
            onDeleteAccountClick={() => forms.setDeleteAccountConfirm(true)}
            onDeleteAccountCancel={() => forms.setDeleteAccountConfirm(false)}
            onDeleteAccountConfirm={forms.handleAdminDeleteAccount}
          />
        );
      
      case 'messages':
        return (
          <section className={styles.section}>
            <MessagesSection token={window.localStorage.getItem('policyora_token') ?? ''} />
          </section>
        );

      case 'search':
        return (
          <section className={styles.section}>
            <EnterpriseSearchPage />
          </section>
        );

      case 'chat':
        return (
          <section className={styles.sectionChat}>
            <EnterpriseChatPage />
          </section>
        );

      case 'chat-history':
        return (
          <section className={styles.section}>
            <EnterpriseChatHistoryPage />
          </section>
        );

      case 'search-history':
        return (
          <section className={styles.section}>
            <EnterpriseSearchHistoryPage />
          </section>
        );

      case 'regulations':
        return (
          <section className={styles.section}>
            <EnterpriseRegulationsPage adminMode />
          </section>
        );

      case 'trending':
        return <AdminTrendingWorkspace />;

      case 'charts':
        return (
          <section className={styles.section}>
            {isRegulationsBreakdown ? <RegulationsBreakdownPage /> : <EnterpriseChartsPage />}
          </section>
        );

      case 'newsletters':
        return (
          <section className={styles.section}>
            <EnterpriseNewslettersPage />
          </section>
        );

      default:
        return null;
    }
  };

  return (
    <DashboardLayout
      collapsed={sidebarCollapsed}
      activeSection={isRegulationsBreakdown ? 'charts' : activeSection}
      availableSections={allowedSections}
      allowedFeatures={
        // While admin data hasn't loaded yet, pass empty array so no page links
        // flash in the sidebar before we know what the role actually allows.
        // Once loaded: null = default admin (show all), array = custom role (filtered).
        !currentAdminSummary
          ? []
          : currentAdminSummary.custom_role_id
            ? (currentAdminSummary.allowed_features ?? [])
            : null
      }
      welcomeLabel={activeSection === 'overview' ? 'Statistics dashboard' : profileMeta.welcomeLabel}
      selectedRegulation={selectedRegulation}
      onToggleSidebar={() => setSidebarCollapsed(prev => !prev)}
      onNavClick={handleNavClick}
      onProfileClick={() => handleNavClick('profile')}
      onLogout={handleLogout}
      onRegulationModalClose={() => setSelectedRegulation(null)}
    >
      {getSectionContent()}
    </DashboardLayout>
  );
}
